import React from 'react';
import { Link } from 'react-router';
import type { LscArchiveItem } from '../../types';
import { lscPath } from '../../routing';
import { useLeague } from '../../hooks/useLeague';
import { formatArchiveCardMeta, getArchiveDisplayTitle } from './leagueHomeUtils';
import { otherBroadcastLabel, otherBroadcasts } from './archiveCardSources';
import ArchiveThumbnail from './ArchiveThumbnail';

export type ArchiveReplayCardProps = {
  leagueId: string;
  archive: LscArchiveItem;
  compact?: boolean;
  onSelect?: (archive: LscArchiveItem) => void;
};

const ArchiveReplayCard: React.FC<ArchiveReplayCardProps> = ({
  leagueId,
  archive,
  compact = false,
  onSelect,
}) => {
  const { league } = useLeague();
  const title = getArchiveDisplayTitle(archive);
  const meta = formatArchiveCardMeta(archive, league?.active_edition);
  const detailPath = lscPath(`/leagues/${leagueId}/archive/${archive.id}`);
  const others = otherBroadcasts(archive);

  return (
    <article
      className={`shrink-0 rounded-xl border border-secondary-700 bg-secondary-800/50 overflow-hidden ${
        compact ? 'w-56' : 'w-72'
      }`}
      data-testid="archive-replay-card"
    >
      <div className="aspect-video bg-secondary-900 relative">
        <ArchiveThumbnail archive={archive} compact={compact} />
      </div>
      <div className="space-y-2 p-3">
        <h3 className="text-sm font-semibold text-white line-clamp-2">{title}</h3>
        {meta ? <p className="text-xs text-secondary-500">{meta}</p> : null}
        <div className="flex flex-wrap items-center gap-2">
          {onSelect ? (
            <button
              type="button"
              className="btn-secondary inline-flex text-xs"
              data-testid="archive-replay-watch-cta"
              onClick={() => onSelect(archive)}
            >
              Watch replay
            </button>
          ) : (
            <Link
              to={detailPath}
              className="btn-secondary inline-flex text-xs"
              data-testid="archive-replay-watch-cta"
            >
              Watch replay
            </Link>
          )}
          {onSelect ? (
            <Link
              to={detailPath}
              className="text-xs text-primary-300 hover:text-primary-200"
              data-testid="archive-replay-open-detail"
            >
              Open archive page
            </Link>
          ) : null}
          {others.map((source) => {
            const label = otherBroadcastLabel(others, source);
            const sourcePath = lscPath(`/leagues/${leagueId}/archive/${source.id}`);
            return onSelect ? (
              <button
                key={source.id}
                type="button"
                className="btn-secondary inline-flex text-xs"
                data-testid="archive-replay-other-broadcast"
                onClick={() => onSelect(source)}
              >
                {label}
              </button>
            ) : (
              <Link
                key={source.id}
                to={sourcePath}
                className="btn-secondary inline-flex text-xs"
                data-testid="archive-replay-other-broadcast"
              >
                {label}
              </Link>
            );
          })}
        </div>
      </div>
    </article>
  );
};

export default ArchiveReplayCard;
